/* ============================================================================
   Curated Collection JS  —  v2026-06-20  (HistoHead)
   Non-destructive version.

   Purpose:
   • If URL has ?first=product-handle or ?first=product-url, pin that product
     to slot #1 on the collection page.
   • If the product already exists in the rendered collection grid, move it.
   • If the product is not on the first rendered page, fetch only that product
     card and insert it at the top.
   • Uses same-origin credentials for Shopify Markets/localized pricing.
   • Does NOT rebuild the full collection grid.
============================================================================ */

(() => {
  const GRID_SEL = 'ul#product-grid, ul[id^="product-grid"], .product-grid';
  const qp = new URLSearchParams(location.search);

  /* ---- 0. Helpers -------------------------------------------------- */

  const safeDecode = value => {
    try {
      return decodeURIComponent(value || '');
    } catch (_) {
      return value || '';
    }
  };

  const handleFromHref = href => {
    try {
      const u = new URL(href, location.origin);
      const segs = u.pathname.split('/').filter(Boolean);
      const productIndex = segs.indexOf('products');
      return productIndex > -1 ? decodeURIComponent(segs[productIndex + 1] || '') : '';
    } catch (_) {
      return '';
    }
  };

  const cssEscape = value => {
    if (window.CSS && typeof CSS.escape === 'function') return CSS.escape(value);
    return String(value).replace(/"/g, '\\"');
  };

  /* ---- 1. Query-string parsing ------------------------------------- */

  let rawFirst = safeDecode((qp.get('first') || '').trim());
  let firstHandle = '';
  let variantFromURL = '';

  if (rawFirst.startsWith('http')) {
    try {
      const u = new URL(rawFirst);
      const segs = u.pathname.split('/').filter(Boolean);

      if (segs[0] === 'products' && segs[1]) {
        firstHandle = segs[1];
      }

      variantFromURL = u.searchParams.get('variant') || '';
    } catch (_) {
      firstHandle = '';
    }
  } else {
    const segs = rawFirst.split('/').filter(Boolean);
    firstHandle = segs.pop() || '';
  }

  if (!firstHandle) return;

  const variantCandidate = qp.get('variant') || variantFromURL || '';
  const variantID = /^\d+$/.test(variantCandidate) ? variantCandidate : '';

  const grid = document.querySelector(GRID_SEL);
  if (!grid) return;

  const initialCardCount = grid.querySelectorAll('li.grid__item').length;

  /* ---- 2. Theme-editor preview support ----------------------------- */

  const previewExtras = (() => {
    const list = [];

    ['preview_theme_id', 'preview_theme_token', 'pb'].forEach(k => {
      if (qp.has(k)) {
        list.push(`${k}=${encodeURIComponent(qp.get(k) || '')}`);
      }
    });

    return list.join('&');
  })();

  const appendPreview = url =>
    previewExtras
      ? (url.includes('?') ? `${url}&${previewExtras}` : `${url}?${previewExtras}`)
      : url;

  /* ---- 3. Card view selection -------------------------------------- */

  const aspect = (grid.getAttribute('data-aspect') || '').toLowerCase();

  const view =
    aspect === 'square' ? 'card-square' :
    aspect === 'adapt'  ? 'card' :
    'card-portrait';

  /* ---- 4. Fetch one fallback card only ------------------------------ */

  const fetchCard = async (handle, variant = '') => {
    const url = appendPreview(
      `/products/${encodeURIComponent(handle)}?view=${view}${variant ? `&variant=${variant}` : ''}`
    );

    const r = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'text/html'
      }
    });

    if (!r.ok) return '';

    const html = await r.text();
    return html.trim() ? html : '';
  };

  /* ---- 5. Find, move, and clean cards ------------------------------- */

  const findGridItemByHandle = handle => {
    const direct = grid.querySelector(
      `li.grid__item[data-product-handle="${cssEscape(handle)}"]`
    );

    if (direct) return direct;

    const links = Array.from(grid.querySelectorAll('a[href*="/products/"]'));
    const link = links.find(a => handleFromHref(a.href) === handle);

    return link ? link.closest('li.grid__item') : null;
  };

  const removeDuplicatePinnedCards = pinnedLi => {
    const items = Array.from(grid.querySelectorAll('li.grid__item'));

    items.forEach(item => {
      if (item === pinnedLi) return;

      const itemHandle =
        item.getAttribute('data-product-handle') ||
        handleFromHref(item.querySelector('a[href*="/products/"]')?.href || '');

      if (itemHandle === firstHandle) {
        item.remove();
      }
    });
  };

  const keepOriginalCardCount = pinnedLi => {
    const items = Array.from(grid.querySelectorAll('li.grid__item'));

    if (initialCardCount && items.length > initialCardCount) {
      const last = items[items.length - 1];

      if (last && last !== pinnedLi) {
        last.remove();
      }
    }
  };

  /* ---- 6. Variant image and link handling --------------------------- */

  const applyVariantToPinnedCard = async pinnedLi => {
    if (!variantID || !pinnedLi) return;

    try {
      const prod = await fetch(`/products/${encodeURIComponent(firstHandle)}.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        }
      }).then(r => r.json());

      const varObj = prod.variants.find(v => String(v.id) === variantID);
      if (!varObj || !varObj.featured_image || !varObj.featured_image.src) return;

      const newSrcBase = varObj.featured_image.src;

      pinnedLi.querySelectorAll('img').forEach(img => {
        let width = img.getAttribute('width') || '';

        if (!width && img.currentSrc) {
          const widthMatch = img.currentSrc.match(/[?&]width=(\d+)/);
          if (widthMatch) width = widthMatch[1];
        }

        if (!width && img.src) {
          const oldShopifySizeMatch = img.src.match(/_(\d+x)\./);
          if (oldShopifySizeMatch) width = oldShopifySizeMatch[1].replace('x', '');
        }

        width = width || '600';

        try {
          const u = new URL(newSrcBase, location.origin);
          u.searchParams.set('width', width);

          img.src = u.toString();
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
        } catch (_) {
          img.src = newSrcBase;
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
        }
      });

      pinnedLi.querySelectorAll('a[href*="/products/"]').forEach(a => {
        const u = new URL(a.href, location.origin);
        u.searchParams.set('variant', variantID);
        a.href = u.pathname + u.search;
      });
    } catch (_) {
      /* silent */
    }
  };

  /* ---- 7. Main ------------------------------------------------------ */

  (async () => {
    let pinnedLi = findGridItemByHandle(firstHandle);

    /*
      Fast path:
      Product is already in the server-rendered collection grid.
      Move the existing Shopify-rendered card to slot #1.
    */
    if (pinnedLi) {
      grid.prepend(pinnedLi);
      pinnedLi.classList.add('hh-curated-pinned');

      removeDuplicatePinnedCards(pinnedLi);
      await applyVariantToPinnedCard(pinnedLi);

      return;
    }

    /*
      Fallback path:
      Product is not on the first rendered collection page.
      Fetch only that one product card, using same-origin credentials so
      Shopify Markets pricing/currency stays consistent.
    */
    const pinnedHTML = await fetchCard(firstHandle, variantID);
    if (!pinnedHTML) return;

    grid.insertAdjacentHTML('afterbegin', pinnedHTML);

    pinnedLi = grid.querySelector('li.grid__item:first-child');
    if (!pinnedLi) return;

    pinnedLi.setAttribute('data-product-handle', firstHandle);
    pinnedLi.classList.add('hh-curated-pinned');

    removeDuplicatePinnedCards(pinnedLi);
    keepOriginalCardCount(pinnedLi);
    await applyVariantToPinnedCard(pinnedLi);
  })();
})();